import type {
  AuthUser,
  AppSettings,
  JobRow,
  CrmCatalog,
  LeadActivity,
  LeadTask,
  LeadReport,
  Manager,
  MergedLeadRef,
  LeadFilters,
  LeadStats,
  LeadView,
  MeetingDetail,
  MeetingInput,
  MeetingListItem,
  OfflineMeetingInput,
  Person,
  PersonInput,
  MailResult,
  StaffUser,
  SystemStatus,
  TelegramSelf,
  UserCreateInput,
} from "./types";

/** Фильтры лидов в строку запроса — пустые значения не отправляем. */
function leadQuery(filters: LeadFilters): string {
  const q = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      q.set(key, String(value));
    }
  }
  return q.toString();
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

async function call<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    credentials: "include", // кука сессии
    headers: {
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...init.headers,
    },
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const data = text ? JSON.parse(text) : null;

  if (!res.ok) {
    // Сессия кончилась или доступ закрыли, пока страница была открыта —
    // сообщаем приложению, чтобы оно вернуло на экран входа.
    if (res.status === 401 && !path.startsWith("/auth/")) {
      window.dispatchEvent(new CustomEvent("zmh:signed-out"));
    }
    throw new ApiError(
      res.status,
      data?.error ?? `Ошибка ${res.status}`,
      data?.details,
    );
  }
  return data as T;
}

export const api = {
  // ── вход и учётные записи ───────────────────────────────────────────────
  login: (login: string, password: string) =>
    call<{ user: AuthUser }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ login, password }),
    }).then((r) => r.user),
  logout: () => call<{ ok: true }>("/auth/logout", { method: "POST" }),
  me: () => call<{ user: AuthUser }>("/auth/me").then((r) => r.user),
  changePassword: (current: string, next: string) =>
    call<{ user: AuthUser }>("/auth/password", {
      method: "POST",
      body: JSON.stringify({ current_password: current, new_password: next }),
    }).then((r) => r.user),

  listUsers: () => call<{ users: StaffUser[] }>("/users").then((r) => r.users),
  createUser: (input: UserCreateInput) =>
    call<{ user: StaffUser; mail: MailResult }>("/users", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  updateUser: (id: number, input: { name: string; email: string }) =>
    call<{ user: StaffUser }>(`/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }).then((r) => r.user),
  setUserPassword: (id: number, password: string, sendEmail = false) =>
    call<{ user: StaffUser; mail: MailResult }>(`/users/${id}/password`, {
      method: "POST",
      body: JSON.stringify({ password, send_email: sendEmail }),
    }),

  /** Проверка почты: письмо уходит на адрес администратора. */
  testMail: () =>
    call<{ ok: boolean; reason: string }>("/users/mail/test", { method: "POST" }),
  setUserDisabled: (id: number, disabled: boolean) =>
    call<{ user: StaffUser }>(`/users/${id}/disable`, {
      method: "POST",
      body: JSON.stringify({ disabled }),
    }).then((r) => r.user),
  setUserOwnLeadsOnly: (id: number, on: boolean) =>
    call<{ user: StaffUser }>(`/users/${id}/own-leads-only`, {
      method: "POST",
      body: JSON.stringify({ on }),
    }).then((r) => r.user),
  setUserPrivateLeads: (id: number, on: boolean) =>
    call<{ user: StaffUser }>(`/users/${id}/private-leads`, {
      method: "POST",
      body: JSON.stringify({ on }),
    }).then((r) => r.user),
  deleteUser: (id: number) => call<void>(`/users/${id}`, { method: "DELETE" }),

  // ── телеграм ────────────────────────────────────────────────────────────
  userTelegramLink: (id: number) =>
    call<{ user: StaffUser; link_url: string | null }>(`/users/${id}/telegram-link`),
  userTelegramUnlink: (id: number) =>
    call<{ user: StaffUser }>(`/users/${id}/telegram-unlink`, { method: "POST" }),
  telegramSelf: () => call<TelegramSelf>("/telegram/me"),
  telegramUnlinkSelf: () =>
    call<{ ok: true }>("/telegram/unlink", { method: "POST" }),

  // ── настройки ───────────────────────────────────────────────────────────
  getSettings: () => call<AppSettings>("/settings"),
  setReminderLead: (minutes: number) =>
    call<AppSettings>("/settings", {
      method: "PATCH",
      body: JSON.stringify({ reminder_lead_minutes: minutes }),
    }),
  setCurrentEvent: (name: string) =>
    call<AppSettings>("/settings", {
      method: "PATCH",
      body: JSON.stringify({ current_event: name }),
    }),

  setDigest: (patch: { digest_time?: string; digest_enabled?: boolean }) =>
    call<AppSettings>("/settings", { method: "PATCH", body: JSON.stringify(patch) }),

  /** Разослать утреннюю сводку прямо сейчас — посмотреть, как она выглядит. */
  sendDigestNow: () =>
    call<{ sent: number; skipped: number }>("/settings/digest/test", { method: "POST" }),

  // ── люди ────────────────────────────────────────────────────────────────
  listPeople: (params: { search?: string; kind?: string; archived?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.search) q.set("search", params.search);
    if (params.kind && params.kind !== "all") q.set("kind", params.kind);
    if (params.archived) q.set("archived", "1");
    return call<{ people: Person[] }>(`/people?${q}`).then((r) => r.people);
  },
  createPerson: (input: PersonInput) =>
    call<{ person: Person }>("/people", {
      method: "POST",
      body: JSON.stringify(input),
    }).then((r) => r.person),
  updatePerson: (id: number, input: PersonInput) =>
    call<{ person: Person }>(`/people/${id}`, {
      method: "PUT",
      body: JSON.stringify(input),
    }).then((r) => r.person),
  archivePerson: (id: number, archived: boolean) =>
    call<{ person: Person }>(`/people/${id}/archive`, {
      method: "POST",
      body: JSON.stringify({ archived }),
    }).then((r) => r.person),
  deletePerson: (id: number) => call<void>(`/people/${id}`, { method: "DELETE" }),

  // ── встречи ─────────────────────────────────────────────────────────────
  listMeetings: (scope: "upcoming" | "past" | "all") =>
    call<{ meetings: MeetingListItem[] }>(`/meetings?scope=${scope}`).then(
      (r) => r.meetings,
    ),
  getMeeting: (id: number) =>
    call<{ meeting: MeetingDetail }>(`/meetings/${id}`).then((r) => r.meeting),
  createMeeting: (input: MeetingInput) =>
    call<{ meeting: MeetingDetail }>("/meetings", {
      method: "POST",
      body: JSON.stringify(input),
    }).then((r) => r.meeting),
  updateMeeting: (id: number, input: MeetingInput) =>
    call<{ meeting: MeetingDetail }>(`/meetings/${id}`, {
      method: "PUT",
      body: JSON.stringify(input),
    }).then((r) => r.meeting),
  cancelMeeting: (id: number) =>
    call<{ meeting: MeetingDetail }>(`/meetings/${id}/cancel`, { method: "POST" }).then(
      (r) => r.meeting,
    ),
  deleteMeeting: (id: number) =>
    call<void>(`/meetings/${id}`, { method: "DELETE" }),

  // ── очные встречи и карточки клиентов ───────────────────────────────────
  createOfflineMeeting: (input: OfflineMeetingInput) =>
    call<{ meeting: MeetingDetail }>("/meetings/offline", {
      method: "POST",
      body: JSON.stringify(input),
    }).then((r) => r.meeting),

  /** Файл уходит сырым телом: имя передаём заголовком, multipart не нужен. */
  uploadAudio: async (id: number, file: File) => {
    const res = await fetch(`/api/meetings/${id}/audio`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": file.type || "application/octet-stream",
        "X-File-Name": encodeURIComponent(file.name).replace(/%20/g, "_"),
      },
      body: file,
    });
    const text = await res.text();
    const data = text ? JSON.parse(text) : null;
    if (!res.ok) {
      throw new ApiError(res.status, data?.error ?? `Ошибка ${res.status}`);
    }
    return data.meeting as MeetingDetail;
  },

  leadCatalog: () => call<CrmCatalog>("/leads/catalog"),

  listLeads: (filters: LeadFilters = {}) =>
    call<{ leads: LeadView[]; stats: LeadStats; archived_matches: number }>(
      `/leads?${leadQuery(filters)}`,
    ),

  getLead: (meetingId: number) =>
    call<{ lead: LeadView; duplicates: LeadView[]; merged: MergedLeadRef[] }>(
      `/leads/${meetingId}`,
    ),

  managers: () =>
    call<{ managers: Manager[] }>("/leads/managers").then((r) => r.managers),

  setLeadArchived: (meetingId: number, archived: boolean) =>
    call<{ lead: LeadView }>(`/leads/${meetingId}/archive`, {
      method: "POST",
      body: JSON.stringify({ archived }),
    }).then((r) => r.lead),
  transferLead: (meetingId: number, toUserId: number, comment: string) =>
    call<{ lead: LeadView }>(`/leads/${meetingId}/transfer`, {
      method: "POST",
      body: JSON.stringify({ to_user_id: toUserId, comment }),
    }).then((r) => r.lead),

  leadReport: (event: string) =>
    call<LeadReport>(`/leads/report${event ? `?event=${encodeURIComponent(event)}` : ""}`),

  setStale: (patch: { stale_days?: number; stale_weekly_enabled?: boolean }) =>
    call<AppSettings>("/settings", { method: "PATCH", body: JSON.stringify(patch) }),

  sendStaleNow: () =>
    call<{ sent: number; stale: number }>("/settings/stale/test", { method: "POST" }),

  leadTasks: (meetingId: number) =>
    call<{ tasks: LeadTask[] }>(`/leads/${meetingId}/tasks`).then((r) => r.tasks),

  addLeadTask: (meetingId: number, text: string, dueAt: string | null) =>
    call<{ task: LeadTask }>(`/leads/${meetingId}/tasks`, {
      method: "POST",
      body: JSON.stringify({ text, due_at: dueAt }),
    }).then((r) => r.task),

  patchLeadTask: (
    meetingId: number,
    id: number,
    patch: { text?: string; due_at?: string | null; done?: boolean },
  ) =>
    call<{ task: LeadTask }>(`/leads/${meetingId}/tasks/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }).then((r) => r.task),

  deleteLeadTask: (meetingId: number, id: number) =>
    call<{ ok: true }>(`/leads/${meetingId}/tasks/${id}`, { method: "DELETE" }),

  /** Присоединить дубль к этой карточке. Ничего не удаляется, можно отменить. */
  mergeLead: (meetingId: number, donorId: number) =>
    call<{ lead: LeadView; merged: MergedLeadRef[] }>(
      `/leads/${meetingId}/merge/${donorId}`,
      { method: "POST" },
    ),

  unmergeLead: (meetingId: number, donorId: number) =>
    call<{ lead: LeadView; merged: MergedLeadRef[] }>(
      `/leads/${meetingId}/unmerge/${donorId}`,
      { method: "POST" },
    ),

  updateLead: (meetingId: number, patch: Partial<Record<string, unknown>>) =>
    call<{ lead: LeadView }>(`/leads/${meetingId}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }).then((r) => r.lead),

  unlockLeadField: (meetingId: number, field: string) =>
    call<{ lead: LeadView }>(`/leads/${meetingId}/unlock/${field}`, {
      method: "POST",
    }).then((r) => r.lead),

  leadActivities: (meetingId: number) =>
    call<{ activities: LeadActivity[] }>(`/leads/${meetingId}/activities`).then(
      (r) => r.activities,
    ),

  addLeadActivity: (meetingId: number, kind: string, text: string) =>
    call<{ activity: LeadActivity }>(`/leads/${meetingId}/activities`, {
      method: "POST",
      body: JSON.stringify({ kind, text }),
    }).then((r) => r.activity),

  deleteLeadActivity: (meetingId: number, id: number) =>
    call<{ ok: true }>(`/leads/${meetingId}/activities/${id}`, { method: "DELETE" }),

  /** Просит модель составить письмо клиенту заново. Идёт долго — секунд двадцать. */
  draftLeadEmail: (meetingId: number) =>
    call<{ lead: LeadView }>(`/leads/${meetingId}/email`, { method: "POST" }).then(
      (r) => r.lead,
    ),

  leadsExportUrl: (filters: LeadFilters = {}) =>
    `/api/leads/export.csv?${leadQuery(filters)}`,

  syncMeeting: (id: number) => call<{ ok: true }>(`/meetings/${id}/sync`, { method: "POST" }),
  ingestMeeting: (id: number) =>
    call<{ ok: true }>(`/meetings/${id}/ingest`, { method: "POST" }),
  transcribeMeeting: (id: number, force = false) =>
    call<{ ok: true }>(`/meetings/${id}/transcribe`, {
      method: "POST",
      body: JSON.stringify({ force }),
    }),
  analyzeMeeting: (id: number, force = true) =>
    call<{ ok: true }>(`/meetings/${id}/analyze`, {
      method: "POST",
      body: JSON.stringify({ force }),
    }),

  // ── система ─────────────────────────────────────────────────────────────
  status: () => call<SystemStatus>("/status"),
  checkKeys: () =>
    call<{
      zoom: { ok: boolean; user?: string; error?: string };
      anthropic: { ok: boolean; error?: string };
    }>("/status/check", { method: "POST" }),
  listJobs: () =>
    call<{ jobs: JobRow[]; stats: Record<string, number> }>("/jobs"),
  retryJob: (id: number) => call<{ ok: true }>(`/jobs/${id}/retry`, { method: "POST" }),
  sweep: () => call<{ ok: true }>("/sweep", { method: "POST" }),
};
